/* eslint-disable global-require */
import MaterialIcons from '@expo/vector-icons/MaterialIcons';

export const fonts = {
  ...MaterialIcons.font,
  'Flame-Bold': require('../../ucl/themes/bk/fonts/FlameBold.ttf'),
  'Flame-Regular': require('../../ucl/themes/bk/fonts/FlameRegular.ttf'),
  'Flame-RegularSans': require('../../ucl/themes/bk/fonts/FlameRegularSans.ttf'),
  Tiki: require('../../ucl/themes/bk/fonts/KonTikiJF-Aloha.otf'),
};

export const fontSizes = {
  '2xs': 10,
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30,
  '4xl': 36,
  '5xl': 48,
  '6xl': 60,
  '7xl': 72,
  '8xl': 96,
  '9xl': 128,
};

export const fontConfig = {
  Flame: {
    400: {
      normal: 'Flame-Regular',
      // There is no italic version of this font
      // Setting to the same as normal so that we don't break iOS
      italic: 'Flame-Regular',
    },
    700: {
      normal: 'Flame-Bold',
      // There is no italic version of this font
      // Setting to the same as normal so that we don't break iOS
      italic: 'Flame-Bold',
    },
  },
  'Flame-Bold': {
    // Force bold font regardless of what font-weight is set
    400: {
      normal: 'Flame-Bold',
      // There is no italic version of this font
      // Setting to the same as normal so that we don't break iOS
      italic: 'Flame-Bold',
    },
    700: {
      normal: 'Flame-Bold',
      // There is no italic version of this font
      // Setting to the same as normal so that we don't break iOS
      italic: 'Flame-Bold',
    },
  },
  'Flame-Sans': {
    400: {
      normal: 'Flame-RegularSans',
      // There is no italic version of this font
      // Setting to the same as normal so that we don't break iOS
      italic: 'Flame-RegularSans',
    },
    // We might need to change this in the future, but design has said
    // 1) There is no existing Flame-BoldSans font file
    // 2) They sometimes use Flame-Regular as the bold variant
    700: {
      normal: 'Flame-Regular',
      italic: 'Flame-Regular',
    },
  },
};

// make it not too huge on os font scaling or else our layouts become broken
export const HEADER_MAX_FONT_SIZE_MULTIPLIER = 1.7; // headers are particularly sensitive to this, smaller value
export const ACTION_BUTTON_MAX_FONT_SIZE_MULTIPLIER = 2.2; // action buttons are particularly sensitive to this, smaller value
export const BODY_MAX_FONT_SIZE_MULTIPLIER = 2.5; // bodies are less sensitive to this, larger value

/** in silly nativebase format (combines styles and props) but now shared to new Typography component
 * TODO REMOVE and consolidate into Typography after nativebase Text removal in a better structure
 */
const coreTextVariants = {
  hero: {
    accessibilityRole: 'header',
    fontFamily: 'heading',
    fontSize: [32, 32, 32, 56],
    lineHeight: [36, 36, 36, 56],
    mb: 3, // TODO change to marginBottom after nativebase Text removal
    maxFontSizeMultiplier: HEADER_MAX_FONT_SIZE_MULTIPLIER,
  },
  headerOne: {
    accessibilityRole: 'header',
    fontFamily: 'heading',
    fontSize: [24, 24, 24, 30],
    lineHeight: [24, 24, 24, 32],
    mb: 3, // TODO change to marginBottom after nativebase Text removal
    maxFontSizeMultiplier: HEADER_MAX_FONT_SIZE_MULTIPLIER,
  },
  headerTwo: {
    accessibilityRole: 'header',
    accessibilityLevel: 2,
    fontFamily: 'heading',
    fontSize: [18, 18, 18, 24],
    lineHeight: [20, 20, 20, 28],
    mb: 3, // TODO change to marginBottom after nativebase Text removal
    maxFontSizeMultiplier: HEADER_MAX_FONT_SIZE_MULTIPLIER,
  },
  headerThree: {
    accessibilityRole: 'header',
    accessibilityLevel: 3,
    fontFamily: 'heading',
    fontSize: [16, 16, 16, 18],
    lineHeight: 20,
    mb: 3, // TODO change to marginBottom after nativebase Text removal
    maxFontSizeMultiplier: HEADER_MAX_FONT_SIZE_MULTIPLIER,
  },
  headerFour: {
    accessibilityRole: 'header',
    accessibilityLevel: 4,
    fontFamily: 'heading',
    fontSize: 13,
    lineHeight: 16,
    mb: 3, // TODO change to marginBottom after nativebase Text removal
    maxFontSizeMultiplier: HEADER_MAX_FONT_SIZE_MULTIPLIER,
  },
  copyOne: {
    fontFamily: 'body',
    fontSize: [15, 15, 15, 16],
    lineHeight: [20, 20, 20, 24],
    maxFontSizeMultiplier: BODY_MAX_FONT_SIZE_MULTIPLIER,
  },
  copyTwo: {
    fontFamily: 'body',
    fontSize: [12, 12, 12, 13],
    lineHeight: [16, 16, 16, 20],
    maxFontSizeMultiplier: BODY_MAX_FONT_SIZE_MULTIPLIER,
  },
  bodyOne: {
    fontFamily: 'body',
    fontSize: [13, 13, 13, 14],
    lineHeight: [16, 16, 16, 16],
    maxFontSizeMultiplier: BODY_MAX_FONT_SIZE_MULTIPLIER,
  },
  formUtility: {
    fontFamily: 'body',
    fontSize: 11,
    lineHeight: 12,
    maxFontSizeMultiplier: BODY_MAX_FONT_SIZE_MULTIPLIER,
  },
  link: {
    fontFamily: 'body',
    fontSize: 14,
    maxFontSizeMultiplier: BODY_MAX_FONT_SIZE_MULTIPLIER,
    textDecorationLine: 'underline',
    cursor: 'pointer',
    accessibilityRole: 'link',
  },
};

const noRoleOrLevel = {
  accessibilityRole: undefined,
  accessibilityLevel: undefined,
};

// variants that don't have a header a11y role
const lookVariants = {
  headerOneLook: {
    ...coreTextVariants.headerOne,
    ...noRoleOrLevel,
  },
  headerTwoLook: {
    ...coreTextVariants.headerTwo,
    ...noRoleOrLevel,
  },
  headerThreeLook: {
    ...coreTextVariants.headerThree,
    ...noRoleOrLevel,
  },
  headerFourLook: {
    ...coreTextVariants.headerFour,
    ...noRoleOrLevel,
  },
};

export const textVariants = {
  ...coreTextVariants,
  ...lookVariants,
};

export type HeaderAccessibilityLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type HeaderVariant =
  | 'hero'
  | 'headerOne'
  | 'headerTwo'
  | 'headerThree'
  | 'headerFour'
  | 'headerOneLook'
  | 'headerTwoLook'
  | 'headerThreeLook'
  | 'headerFourLook';

export type TextVariant = HeaderVariant | 'copyOne' | 'copyTwo' | 'bodyOne' | 'formUtility';
